import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Close as CloseIcon,
  Speed as SpeedIcon,
  Warning as WarningIcon,
  DirectionsCar as CarIcon,
  LocalGasStation as FuelIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Driver, DriverAnalytics } from '../types';
import { driverAPI } from '../services/api';

interface Props {
  open: boolean;
  driver: Driver | null;
  onClose: () => void;
}

const DriverAnalyticsDialog: React.FC<Props> = ({ open, driver, onClose }) => {
  const [analytics, setAnalytics] = useState<DriverAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && driver) {
      loadAnalytics();
    }
  }, [open, driver]);

  const loadAnalytics = async () => {
    if (!driver) return;
    
    setLoading(true);
    setError('');
    try {
      const data = await driverAPI.getDriverAnalytics(driver._id);
      setAnalytics(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const getSafetyScoreColor = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Driver Analytics</Typography>
            {driver && (
              <Typography variant="body2" color="text.secondary">
                {driver.firstName} {driver.lastName} - Last 7 Days
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : analytics ? (
          <Box>
            {/* Safety Score */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="h2" color={`${getSafetyScoreColor(analytics.driver.safetyScore)}.main`}>
                {analytics.driver.safetyScore}
              </Typography>
              <Typography variant="h6" color="text.secondary">Safety Score</Typography>
              <Chip
                label={
                  analytics.driver.safetyScore >= 90 ? 'Excellent Driver' :
                  analytics.driver.safetyScore >= 70 ? 'Good Driver' :
                  'Needs Improvement'
                }
                color={getSafetyScoreColor(analytics.driver.safetyScore) as any}
                sx={{ mt: 1 }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Key Metrics */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CarIcon color="primary" />
                      <Typography variant="caption" color="text.secondary">Total Distance</Typography>
                    </Box>
                    <Typography variant="h5">{analytics.analytics.totalDistance} km</Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <SpeedIcon color="primary" />
                      <Typography variant="caption" color="text.secondary">Avg Speed</Typography>
                    </Box>
                    <Typography variant="h5">{analytics.analytics.averageSpeed} km/h</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Max: {analytics.analytics.maxSpeed} km/h
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <WarningIcon color="warning" />
                      <Typography variant="caption" color="text.secondary">Overspeeding</Typography>
                    </Box>
                    <Typography variant="h5">{analytics.analytics.overspeedingIncidents}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Incidents
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <TimerIcon color="primary" />
                      <Typography variant="caption" color="text.secondary">Driving Time</Typography>
                    </Box>
                    <Typography variant="h5">{analytics.analytics.drivingTimeHours}h</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {analytics.analytics.totalTrips} trips
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Additional Metrics */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>Harsh Braking Events</Typography>
                    <Typography variant="h4">{analytics.analytics.harshBrakingEvents}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>Idle Time</Typography>
                    <Typography variant="h4">{analytics.analytics.idleTimeMinutes} min</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Daily Distance Chart */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Daily Distance Traveled</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={analytics.analytics.dailyBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis label={{ value: 'Distance (km)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Bar dataKey="distance" fill="#1976d2" name="Distance (km)" />
                </BarChart>
              </ResponsiveContainer>
            </Box>

            {/* Daily Speed Chart */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Daily Speed Trends</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={analytics.analytics.dailyBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis label={{ value: 'Speed (km/h)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="avgSpeed" stroke="#1976d2" name="Avg Speed" />
                  <Line type="monotone" dataKey="maxSpeed" stroke="#f44336" name="Max Speed" />
                </LineChart>
              </ResponsiveContainer>
            </Box>

            {/* Daily Breakdown Table */}
            <Box>
              <Typography variant="h6" gutterBottom>Daily Breakdown</Typography>
              <Box sx={{ width: '100%', overflowX: 'auto' }}><Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Distance (km)</TableCell>
                    <TableCell align="right">Trips</TableCell>
                    <TableCell align="right">Avg Speed</TableCell>
                    <TableCell align="right">Max Speed</TableCell>
                    <TableCell align="right">Overspeeding</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.analytics.dailyBreakdown.map((day) => (
                    <TableRow key={day.date}>
                      <TableCell>{new Date(day.date).toLocaleDateString()}</TableCell>
                      <TableCell align="right">{day.distance}</TableCell>
                      <TableCell align="right">{day.trips}</TableCell>
                      <TableCell align="right">{day.avgSpeed} km/h</TableCell>
                      <TableCell align="right">{day.maxSpeed} km/h</TableCell>
                      <TableCell align="right">
                        {day.overspeeding > 0 ? (
                          <Chip size="small" label={day.overspeeding} color="warning" />
                        ) : (
                          <Chip size="small" label="0" color="success" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></Box>
            </Box>
          </Box>
        ) : (
          <Typography>No analytics available</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DriverAnalyticsDialog;